import { memo } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Brain, Dumbbell, Heart, Zap } from 'lucide-react';
import { LineChart, Line } from 'recharts';
import styles from './ReadinessView.module.scss';
import { TeamReadinessAthlete, InjuredAthleteSummary } from '../types';
import { formatFullName } from '../../utils/functions';
import { formatJump } from '../utils/jumpUnit';
import { InjuryBadge } from '../../shared/InjuryBadge/InjuryBadge';
import { ComplaintBadge, ComplaintDetail } from '../../shared/ComplaintBadge/ComplaintBadge';
import { INJURY_BODY_ZONE, INJURY_SEVERITY } from '../../enums/injuries';
import { CONFIDENCE_BADGES, PHASE_BADGES } from '../constants/readiness';
import { getDomainColor, getACWRValueColor } from './readinessColors';

interface ReadinessCardProps {
  item: TeamReadinessAthlete;
  complaintsByAthlete: Record<string, ComplaintDetail[]>;
  injuredMap: Record<string, InjuredAthleteSummary>;
}

/**
 * Card atleta della vista Readiness "card view": l'intera card è un link al
 * profilo (nessun click-cella, a differenza della matrice). Estratta da
 * `TeamReadinessTab` per contenerne le dimensioni (regola ≤600 righe).
 */
function ReadinessCard({ item, complaintsByAthlete, injuredMap }: ReadinessCardProps) {
  const ri = item.ri;
  const hooper = item.metrics?.hooper;
  const cmjVal = item.metrics?.cmj; // number | null
  const fcrVal = item.metrics?.fcr; // number | null
  const hrvVal = item.metrics?.hrv; // number | null
  const alerts = (item as any)?.alerts ?? [];
  const fullName = formatFullName(item.firstName, item.lastName);

  const cmjTooltipValue = cmjVal != null ? `CMJ: ${formatJump(cmjVal, item.metrics?.cmjUnit)}` : '-';

  return (
    <Link to={`/dashboard/users/${item.athleteId}`} className={styles.athleteCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardNameRow}>
          <p className={styles.cardAthlName}>{fullName}</p>
          {ri?.status === 'sotto_stress' && (
            <div className={styles.alertWrapper}>
              <AlertCircle className={styles.alertIcon} />
              <div className={styles.alertTooltip}>
                <div className={styles.alertTooltipInner}>
                  <p className={styles.alertTooltipTitle}>Stato critico - Ridurre carico</p>
                  <p className={styles.alertTooltipDesc}>
                    RI: {ri.value}
                  </p>
                  <div className={styles.alertTooltipArrow}></div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.cardRiRow}>
          {ri ? (
            <div className={styles.flexColEndGap2}>
              <div className={styles.flexRowBaselineGap4}>
                <p
                  className={`${styles.cardRiValue} ${styles.cardRiValueDynamic}`}
                  style={{ '--metric-color': getDomainColor(ri.value) } as CSSProperties}
                >
                  {ri.value}
                </p>
                <p className={styles.cardRiLabel}>RI</p>
              </div>
              <div className={styles.badgeRow}>
                {ri.confidenceBadge && CONFIDENCE_BADGES[ri.confidenceBadge] && (
                  <span
                    title={CONFIDENCE_BADGES[ri.confidenceBadge].hint}
                    className={`${styles.cardBadge} ${styles.cardBadgeHint}`}
                    style={{
                      '--badge-bg': CONFIDENCE_BADGES[ri.confidenceBadge].bg,
                      '--badge-fg': CONFIDENCE_BADGES[ri.confidenceBadge].color,
                    } as CSSProperties}
                  >
                    {CONFIDENCE_BADGES[ri.confidenceBadge].label}
                  </span>
                )}
                {ri.calibrationBadge && (
                  <span
                    className={styles.cardBadge}
                    style={{
                      '--badge-bg': PHASE_BADGES[1].bg,
                      '--badge-fg': PHASE_BADGES[1].color,
                    } as CSSProperties}
                  >
                    {PHASE_BADGES[1].label}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className={`${styles.cardRiValue} ${styles.textMuted}`}>-</p>
          )}
        </div>
      </div>

      <div className={styles.cardDomainsGrid}>
        {/* Mental */}
        <div className={styles.cardDomainCell}>
          <div className={styles.cardDomainIconRow}><Brain className={styles.cardDomainIcon} /></div>
          <p className={`${styles.cardDomainBadge} ${hooper != null ? styles.cardDomainBadgeFilled : styles.textMuted}`}>
            {hooper != null ? '●' : '○'}
          </p>
          {hooper != null && (
            <div className={styles.cardTooltip}>
              <div className={styles.cardTooltipInner}>
                <p className={styles.cardTooltipTitle}>Mentale</p>
                <p className={styles.cardTooltipDesc}>Sonno: {hooper.sleepQuality ?? '-'} / Stress: {hooper.stress ?? '-'} / DOMS: {hooper.doms ?? '-'}</p>
                <div className={styles.cardTooltipArrow}></div>
              </div>
            </div>
          )}
        </div>

        {/* Muscular */}
        <div className={styles.cardDomainCell}>
          <div className={styles.cardDomainIconRow}><Dumbbell className={styles.cardDomainIcon} /></div>
          <p className={`${styles.cardDomainBadge} ${cmjVal != null ? styles.cardDomainBadgeFilled : styles.textMuted}`}>
            {cmjVal != null ? '●' : '○'}
          </p>
          {cmjVal != null && (
            <div className={styles.cardTooltip}>
              <div className={styles.cardTooltipInner}>
                <p className={styles.cardTooltipTitle}>Muscolare</p>
                <p className={styles.cardTooltipDesc}>{cmjTooltipValue}</p>
                <div className={styles.cardTooltipArrow}></div>
              </div>
            </div>
          )}
        </div>

        {/* Cardio */}
        <div className={styles.cardDomainCell}>
          <div className={styles.cardDomainIconRow}><Heart className={styles.cardDomainIcon} /></div>
          <p className={`${styles.cardDomainBadge} ${(hrvVal != null || fcrVal != null) ? styles.cardDomainBadgeFilled : styles.textMuted}`}>
            {(hrvVal != null || fcrVal != null) ? '●' : '○'}
          </p>
          {(hrvVal != null || fcrVal != null) && (
            <div className={styles.cardTooltip}>
              <div className={styles.cardTooltipInner}>
                <p className={styles.cardTooltipTitle}>Cardiovascolare</p>
                <p className={styles.cardTooltipDesc}>HRrest: {fcrVal ?? '-'} / HRV: {hrvVal != null ? hrvVal.toFixed(0) : '-'}</p>
                <div className={styles.cardTooltipArrow}></div>
              </div>
            </div>
          )}
        </div>

        {/* Strain — not available in v2 response */}
        <div className={styles.cardDomainCell}>
          <div className={styles.cardDomainIconRow}><Zap className={styles.cardDomainIcon} /></div>
          <p className={`${styles.cardDomainBadge} ${styles.textMuted}`}>○</p>
        </div>
      </div>

      {item.sparkline && item.sparkline.some((v: any) => v != null) && (
        <div className={styles.cardSparklineRow}>
          <span className={styles.cardSparklineLabel}>7gg</span>
          <LineChart width={100} height={24} data={item.sparkline.map((v: any, i: number) => ({ i, v }))}>
            <Line type="monotone" dataKey="v" stroke="#063444" strokeWidth={1.5} dot={false} connectNulls={false} />
          </LineChart>
        </div>
      )}

      {item.hasComplaints && (
        <div className={styles.cardBadgeWrapper}>
          <ComplaintBadge
            complaints={complaintsByAthlete[item.athleteId]}
            maxIntensity={item.complaintMaxIntensity}
            size="sm"
          />
        </div>
      )}
      {injuredMap[item.athleteId] && (
        <div className={styles.cardBadgeWrapper}>
          <InjuryBadge
            size="sm"
            injury={{
              injuryId: injuredMap[item.athleteId].injuryId,
              athleteId: item.athleteId,
              bodyZone: injuredMap[item.athleteId].bodyZone as INJURY_BODY_ZONE,
              daysOut: injuredMap[item.athleteId].daysOut,
              severity: injuredMap[item.athleteId].severity as INJURY_SEVERITY,
              rtpPhase: injuredMap[item.athleteId].rtpPhase,
              expectedReturnDate: injuredMap[item.athleteId].expectedReturnDate,
            }}
          />
        </div>
      )}

      {item.strain?.acwr != null && (
        <div
          className={styles.cardAcwr}
          style={{ '--metric-color': getACWRValueColor(item.strain.acwr) } as CSSProperties}
        >
          ACWR: {item.strain.acwr.toFixed(2)}
        </div>
      )}

      {alerts.length > 0 && (
        <div className={styles.cardSRPERow}>
          <div className={styles.cardSRPEInner}>
            <p className={`${styles.cardSRPELabel} ${styles.cardAlertText}`}>{alerts[0].message}</p>
            {alerts.length > 1 && (
              <p className={`${styles.cardSRPEValue} ${styles.cardAlertText}`}>+{alerts.length - 1}</p>
            )}
          </div>
        </div>
      )}
    </Link>
  );
}

export default memo(ReadinessCard);
